import React, { useState } from 'react';
import { COLORS } from '../utils/constants.js';
import Badge from '../components/Badge.jsx';

export default function GetResponsePage({ grConnected, setGrConnected, addNotification }) {
  const [apiKey, setApiKey] = useState('');

  const workflow = [
    { step: '1', label: 'Apollo\nProspect', desc: 'Find leads', color: COLORS.blue },
    { step: '→' },
    { step: '2', label: 'Claude AI\nScore & Draft', desc: 'Qualify leads', color: COLORS.purple },
    { step: '→' },
    { step: '3', label: 'GetResponse\nNurture', desc: 'Email campaigns', color: COLORS.green },
    { step: '→' },
    { step: '4', label: 'Convert\n& Close', desc: 'Win deals', color: COLORS.accent },
  ];

  const features = [
    { title: 'Auto-Sync Contacts', desc: 'Push qualified Apollo leads to GetResponse lists.', icon: '🔄', action: 'Sync Contacts' },
    { title: 'Campaign Templates', desc: 'AI-generated email sequences as GetResponse templates.', icon: '📧', action: 'Generate Template' },
    { title: 'Lead Tagging', desc: 'Auto-tag contacts by AI lead score for segmentation.', icon: '🏷️', action: 'Configure Tags' },
    { title: 'Conversion Tracking', desc: 'Track opens, clicks, and conversions to Apollo data.', icon: '📊', action: 'View Analytics' },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}><span style={{ color: COLORS.blue }}>▶</span> GetResponse Integration</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 20 }}>Connect GetResponse for automated email campaigns</p>

      {/* Connection */}
      <div style={{ padding: 24, borderRadius: 12, background: COLORS.surface, border: `1px solid ${grConnected ? COLORS.green : COLORS.accent}44`, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: grConnected ? `${COLORS.green}22` : `${COLORS.accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{grConnected ? '✅' : '🔗'}</div>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{grConnected ? 'Connected to GetResponse' : 'Connect GetResponse'}</h3>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.textMuted }}>{grConnected ? 'API connected and ready' : 'Enter your API key'}</p>
          </div>
        </div>
        {!grConnected && (
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.textDim, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>GetResponse API Key</label>
            <div style={{ display: 'flex', gap: 12 }}>
              <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Enter your GetResponse API key" style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surfaceAlt, color: COLORS.text, fontSize: 13 }} />
              <button onClick={() => { if (apiKey.length > 10) { setGrConnected(true); addNotification('GetResponse connected!', 'success'); } else { addNotification('Enter a valid API key', 'error'); } }} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.blueDim})`, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Connect</button>
            </div>
            <p style={{ fontSize: 12, color: COLORS.textDim, margin: '10px 0 0' }}>Find your API key: GetResponse → Menu → Integrations & API → API</p>
          </div>
        )}
      </div>

      {/* Workflow */}
      <div style={{ padding: 20, borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Sales Pipeline Workflow</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 8 }}>
          {workflow.map((w, i) => w.step === '→' ? (
            <div key={i} style={{ fontSize: 18, color: COLORS.textDim, padding: '0 8px', flexShrink: 0 }}>→</div>
          ) : (
            <div key={i} style={{ padding: 16, borderRadius: 10, border: `1px solid ${w.color}33`, background: `${w.color}11`, textAlign: 'center', minWidth: 140, flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${w.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 14, fontWeight: 700, color: w.color }}>{w.step}</div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: w.color, whiteSpace: 'pre-line' }}>{w.label}</p>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: COLORS.textMuted }}>{w.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
        {features.map((f, i) => (
          <div key={i} style={{ padding: 20, borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 28 }}>{f.icon}</span>
            <h4 style={{ margin: '10px 0 6px', fontSize: 14, fontWeight: 600 }}>{f.title}</h4>
            <p style={{ fontSize: 12, color: COLORS.textMuted, margin: '0 0 14px' }}>{f.desc}</p>
            <button onClick={() => addNotification(grConnected ? `${f.title} initiated` : 'Connect GetResponse first', grConnected ? 'success' : 'error')} style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: 'transparent', color: COLORS.text, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>{f.action}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
